import { inject, observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { CategoryPackageJsonModel } from "../models/BlockTransportSystemJsonModel";
import BlockKitStore from "../stores/BlockKitStore";
import CategoryPackageInfoCard from "../components/CategoryPackageInfoCard/CategoryPackageInfoCard";
import { v4 as uuidv4 } from "uuid";
import { AllCommunityModule, ICellRendererParams, ModuleRegistry } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Badge, Button, Container, Spinner, Stack } from "react-bootstrap";
import PermissionModal from "../PermissionModal/PermissionModal";
import StudioUserStore from "../stores/StudioUserStore";

ModuleRegistry.registerModules([AllCommunityModule]);

export interface IProps {
  blockKitStore?: BlockKitStore;
  studioUserStore?: StudioUserStore;
}

const CategoryPackagePage: React.FC<IProps> = inject("blockKitStore", "studioUserStore")(observer(({
  blockKitStore,
  studioUserStore
}) => {
  const [selectedPackage, setSelectedPackage] = useState<CategoryPackageJsonModel>();
  const [isOpenEditModal, setIsOpenEditModal] = useState<boolean>(false);
  const [isCreateMode, setIsShowCreateMode] = useState<boolean>(false);
  const [isShowPermissionModal, setIsShowPermissionModal] = useState<boolean>(false);
  const [isWorking, setIsWorking] = useState<boolean>(false);
  const { t } = useTranslation();

  useEffect(() => {
    blockKitStore?.initCategoryPackagePageAsync();
  }, []);

  const EditButtonCell: React.FC<ICellRendererParams> = (params) => {
    return (
      <Button
        variant="secondary"
        size="sm"
        onClick={async () => {
          setSelectedPackage(params.data);
          setIsOpenEditModal(true);
          setIsShowCreateMode(false);
          params.api.deselectAll();
          params.node.setSelected(true);
        }}>
        {t("button-edit")}
      </Button>
    );
  }

  return (
    <Container fluid>
      {isOpenEditModal &&
        <CategoryPackageInfoCard
          categoryPackage={selectedPackage}
          isShow={isOpenEditModal}
          isCreateMode={isCreateMode}
          onClickApply={async (categoryPackage: CategoryPackageJsonModel) => {
            await blockKitStore?.studioController.postCategoryPackagesStagingAsync(categoryPackage);
            await blockKitStore?.initCategoryPackagePageAsync();
            setIsOpenEditModal(false);
          }}
          onClickClose={() => {
            setIsOpenEditModal(false);
          }}
        />}
      <PermissionModal<CategoryPackageJsonModel>
        entityType="category-packages"
        entity={selectedPackage}
        isShow={isShowPermissionModal}
        onClose={() => setIsShowPermissionModal(false)}
      />
      <Stack direction="horizontal" gap={3} className="m-2">
        <Button
          variant="primary"
          onClick={() => {
            setSelectedPackage({
              id: uuidv4(),
              lang: [],
              name: "",
              priority: 3,
              viewType: "spreadView"
            });
            setIsShowCreateMode(true);
            setIsOpenEditModal(true);
          }}>
          {t("button-new")}
        </Button>
        <Button
          variant="primary"
          disabled={!selectedPackage || isWorking}
          onClick={async () => {
            setIsWorking(true);
            try {
              if (!confirm(`${selectedPackage?.name} ${t("publish-confirmation-message")}`)) {
                return;
              }

              const response = await blockKitStore?.studioController.publishCategoryPackagesAsync(selectedPackage!);
              if (response?.isSuccess) {
                await blockKitStore?.initCategoryPackagePageAsync();
                alert(t("publish-success"));
              } else {
                alert(`${t("publish-failed")} (${response?.message})`)
              }
            } finally {
              setIsWorking(false);
            }
          }}>
          {isWorking &&
            <Spinner
              className="me-1"
              animation="border"
              size="sm"
              role="status"
            />}
          {t("button-publish")}
        </Button>
        <Button
          variant="danger"
          disabled={!selectedPackage || isWorking}
          onClick={async () => {
            if (!confirm(`${selectedPackage?.name} 삭제 하시겠습니까?`)) {
              return;
            }

            var response = await blockKitStore?.studioController.deleteCategoryPackagesStagingAsync(selectedPackage!);
            if (response.isSuccess) {
              await blockKitStore?.initCategoryPackagePageAsync();
            }

            alert(response.message);
          }}>
          {t("button-delete")}
        </Button>
        <Button
          hidden={!studioUserStore?.hasPermission("categoryPackage:permission")}
          disabled={!selectedPackage}
          variant="warning"
          onClick={() => setIsShowPermissionModal(true)}>{t("button-permission")}</Button>
      </Stack>
      <div style={{ height: "70vh" }}>
        <AgGridReact<CategoryPackageJsonModel>
          getRowId={params => params.data.id!}
          rowData={blockKitStore?.allCategoryPackages}
          columnDefs={[
            {
              headerName: t("publish"),
              width: 80,
              cellRenderer: (params: ICellRendererParams) => {
                return blockKitStore?.publishedCategoryPackages.some(i => i.id === params.data?.id)
                  ? <Badge bg="info">{t("published")}</Badge>
                  : null
              },
            },
            {
              headerName: t("id"),
              filter: true,
              field: "id"
            },
            {
              headerName: t("name"),
              filter: true,
              field: "name",
            },
            {
              headerName: t("priority"),
              filter: true,
              field: "priority"
            },
            {
              headerName: t("view-type"),
              filter: true,
              field: "viewType"
            },
            {
              headerName: t("count"),
              filter: true,
              valueGetter: params => blockKitStore?.allCategories?.filter(c => c.packageId === params.data?.id)?.length
            },
            {
              headerName: t("name-by-country"),
              autoHeight: true,
              filter: true,
              field: "lang",
              cellStyle: {
                "whiteSpaceCollapse": "preserve-breaks",
                "lineHeight": 1.7
              },
              valueGetter: params => params.data?.lang
                ?.filter(l => l.name)
                ?.map(l => `${l.key}( ${l.name} )`)?.join("\n")
            },
            {
              headerName: t("edit"),
              width: 80,
              cellRenderer: EditButtonCell,
            }
          ]}
          rowSelection={{
            mode: "singleRow",
            enableClickSelection: false,
          }}
          onSelectionChanged={(e) => {
            const selected = e.api.getSelectedRows();
            if (selected.length > 0) {
              setSelectedPackage(selected[0]);
            } else {
              setSelectedPackage(undefined);
            }
          }}
          onRowDataUpdated={(e) => {
            const selected = e.api.getSelectedRows();
            if (selected.length > 0) {
              setSelectedPackage(selected[0]);
            }
          }}
        />
      </div>
    </Container>
  )
}));

export default CategoryPackagePage;